import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePWA } from '../context/PWAContext';
import { 
  LayoutDashboard, 
  DoorOpen, 
  Users, 
  CreditCard, 
  BarChart3, 
  LogOut, 
  Menu,
  X,
  User,
  Home,
  Wallet,
  Package,
  MessageSquare,
  Users2,
  ChevronDown,
  Bell,
  UserPlus,
  AlertCircle,
  Wrench,
  Check,
  History,
  Landmark,
  Upload,
  CheckCircle,
  XCircle,
  Settings,
  Download
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import axios from 'axios';
import PushNotificationPrompt from './PushNotification';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const notificationIcons = {
  new_user: UserPlus,
  complaint: MessageSquare,
  payment: CreditCard,
  maintenance: Wrench,
  payment_proof: Upload,
  payment_verified: CheckCircle,
  payment_rejected: XCircle,
  room_request: DoorOpen,
  new_bill: CreditCard,
  bill_created: CreditCard,
  bill: CreditCard,
  overdue_bill: AlertCircle,
  payment_reminder: Bell,
  payment_overdue: AlertCircle,
  registration_approved: CheckCircle,
  registration_rejected: XCircle,
  room_approved: CheckCircle,
  room_rejected: XCircle,
};

const notificationColors = {
  new_user: 'text-blue-600 bg-blue-100',
  complaint: 'text-amber-600 bg-amber-100',
  payment: 'text-red-600 bg-red-100',
  maintenance: 'text-orange-600 bg-orange-100',
  payment_proof: 'text-purple-600 bg-purple-100',
  payment_verified: 'text-green-600 bg-green-100',
  payment_rejected: 'text-red-600 bg-red-100',
  room_request: 'text-indigo-600 bg-indigo-100',
  new_bill: 'text-amber-600 bg-amber-100',
  bill_created: 'text-amber-600 bg-amber-100',
  bill: 'text-amber-600 bg-amber-100',
  overdue_bill: 'text-red-600 bg-red-100',
  payment_reminder: 'text-amber-600 bg-amber-100',
  payment_overdue: 'text-red-600 bg-red-100',
  registration_approved: 'text-green-600 bg-green-100',
  registration_rejected: 'text-red-600 bg-red-100',
  room_approved: 'text-green-600 bg-green-100',
  room_rejected: 'text-red-600 bg-red-100',
};

const NotificationBell = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    if (!user) return;
    try {
      // Use different endpoint based on role
      const endpoint = user?.role === 'penghuni' 
        ? `${API_URL}/api/notifications/tenant`
        : `${API_URL}/api/notifications`;
      const response = await axios.get(endpoint);
      setNotifications(response.data.notifications);
      setUnreadCount(response.data.unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleNotificationClick = async (notification, e) => {
    e.stopPropagation();
    // Mark as read first
    if (!notification.is_read) {
      try {
        await axios.post(`${API_URL}/api/notifications/${notification.id}/read`);
        fetchNotifications();
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    // Navigate to link
    if (notification.link) {
      setIsOpen(false);
      navigate(notification.link);
    }
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API_URL}/api/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit lalu`;
    if (diffHours < 24) return `${diffHours} jam lalu`;
    if (diffDays < 7) return `${diffDays} hari lalu`;
    return date.toLocaleDateString('id-ID');
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative z-50" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 z-[100]">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="font-heading p-0">
            Notifikasi
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">{unreadCount} baru</Badge>
            )}
          </DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs text-primary hover:text-primary"
              onClick={handleMarkAllAsRead}
              data-testid="mark-all-read-btn"
            >
              Tandai semua dibaca
            </Button>
          )}
        </div>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground text-sm">
            Tidak ada notifikasi
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.map((notif) => {
              const Icon = notificationIcons[notif.type] || AlertCircle;
              const colorClass = notificationColors[notif.type] || 'text-gray-600 bg-gray-100';
              const isRead = notif.is_read;
              return (
                <div
                  key={notif.id}
                  className={cn(
                    "flex items-start gap-3 p-3 cursor-pointer hover:bg-accent transition-colors border-l-2",
                    isRead ? "border-l-transparent bg-background" : "border-l-primary bg-primary/5"
                  )}
                  onClick={(e) => handleNotificationClick(notif, e)}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className={cn("w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0", colorClass)}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm", isRead ? "font-normal text-muted-foreground" : "font-semibold")}>{notif.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{notif.message}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatTime(notif.created_at)}</p>
                      </div>
                      {!isRead && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 flex-shrink-0 hover:bg-primary/10"
                          onClick={(e) => handleMarkAsRead(notif.id, e)}
                          title="Tandai sudah dibaca"
                          data-testid={`mark-read-${notif.id}`}
                        >
                          <Check className="w-3.5 h-3.5 text-primary" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const Layout = ({ children }) => {
  const { user, logout, isPemilik } = useAuth();
  const { isInstallable, isInstalled, promptInstall } = usePWA();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [operasionalOpen, setOperasionalOpen] = useState(false);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [sidebarOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleInstallApp = async () => {
    await promptInstall();
  };

  const pemilikMainNav = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/kamar', icon: DoorOpen, label: 'Kamar' },
    { to: '/penghuni', icon: Users, label: 'Penghuni' },
    { to: '/pembayaran', icon: CreditCard, label: 'Pembayaran' },
    { to: '/laporan', icon: BarChart3, label: 'Laporan' },
  ];

  const operasionalNav = [
    { to: '/biaya-operasional', icon: Wallet, label: 'Biaya Operasional' },
    { to: '/fasilitas', icon: Package, label: 'Inventaris' },
    { to: '/pengaduan', icon: MessageSquare, label: 'Pengaduan' },
    { to: '/staff', icon: Users2, label: 'Staff' },
    { to: '/rekening-bank', icon: Landmark, label: 'Rekening Bank' },
    { to: '/audit-log', icon: History, label: 'Riwayat Perubahan' },
  ];

  const penghuniNav = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/kamar-saya', icon: Home, label: 'Kamar Saya' },
    { to: '/pembayaran', icon: CreditCard, label: 'Pembayaran' },
    { to: '/pengaduan', icon: MessageSquare, label: 'Pengaduan' },
  ];

  const navItems = isPemilik() ? pemilikMainNav : penghuniNav;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-card border-r">
        <div className="flex flex-col flex-1 min-h-0">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_kostify-1/artifacts/7od75s9m_Logo_kos_cemara-1-green.png" 
                alt="Kos Cemara Logo"
                className="w-10 h-10 object-contain"
              />
              <div>
                <h1 className="font-heading font-bold text-lg tracking-tight">Kos Cemara</h1>
                <p className="text-xs text-muted-foreground">Manajemen Kost</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => cn(
                  "sidebar-nav-item",
                  isActive && "active"
                )}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
            
            {/* Operasional Section (Pemilik Only) */}
            {isPemilik() && (
              <Collapsible open={operasionalOpen} onOpenChange={setOperasionalOpen} className="mt-4">
                <CollapsibleTrigger className="sidebar-nav-item w-full justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5" />
                    <span className="font-medium">Operasional</span>
                  </div>
                  <ChevronDown className={cn("w-4 h-4 transition-transform", operasionalOpen && "rotate-180")} />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 mt-1 space-y-1">
                  {operasionalNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) => cn(
                        "sidebar-nav-item text-sm py-2",
                        isActive && "active"
                      )}
                    >
                      <item.icon className="w-4 h-4" />
                      <span>{item.label}</span>
                    </NavLink>
                  ))}
                </CollapsibleContent>
              </Collapsible>
            )}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t">
            <div className="flex items-center gap-3 px-4 py-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <NavLink
              to="/profile"
              className={({ isActive }) => cn(
                "sidebar-nav-item mt-2",
                isActive && "active"
              )}
              data-testid="profile-link"
            >
              <Settings className="w-5 h-5" />
              <span className="font-medium">Profil Saya</span>
            </NavLink>
            {isInstallable && !isInstalled && (
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 mt-2 text-primary hover:text-primary hover:bg-primary/10"
                onClick={handleInstallApp}
                data-testid="install-app-btn"
              >
                <Download className="w-5 h-5" />
                Install Aplikasi
              </Button>
            )}
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 mt-2 text-muted-foreground hover:text-destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Keluar
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-background/80 backdrop-blur-lg border-b z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_kostify-1/artifacts/7od75s9m_Logo_kos_cemara-1-green.png" 
            alt="Kos Cemara Logo"
            className="w-8 h-8 object-contain"
          />
          <span className="font-heading font-bold">Kos Cemara</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Desktop Header with Notification Bell */}
      <div className="hidden md:flex fixed top-0 left-64 right-0 h-16 bg-background/80 backdrop-blur-lg border-b z-40 items-center justify-end px-6">
        <NotificationBell />
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setSidebarOpen(false)}
            onTouchMove={(e) => e.preventDefault()}
          />
          <aside 
            className="absolute left-0 top-0 bottom-0 w-64 bg-card animate-slide-in overscroll-contain"
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between h-16 px-6 border-b flex-shrink-0">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_kostify-1/artifacts/7od75s9m_Logo_kos_cemara-1-green.png" 
                    alt="Kos Cemara Logo"
                    className="w-8 h-8 object-contain"
                  />
                  <span className="font-heading font-bold">Kos Cemara</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto overscroll-contain touch-pan-y">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) => cn(
                      "sidebar-nav-item",
                      isActive && "active"
                    )}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </NavLink>
                ))}
                
                {/* Operasional Section (Pemilik Only) - Mobile */}
                {isPemilik() && (
                  <>
                    <div className="pt-4 pb-2">
                      <span className="px-4 text-xs uppercase tracking-widest text-muted-foreground">Operasional</span>
                    </div>
                    {operasionalNav.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) => cn(
                          "sidebar-nav-item",
                          isActive && "active"
                        )}
                      >
                        <item.icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </NavLink>
                    ))}
                  </>
                )}
              </nav>

              <div className="p-4 border-t pb-20">
                <div className="flex items-center gap-3 px-4 py-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                </div>
                <NavLink
                  to="/profile"
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) => cn(
                    "sidebar-nav-item mt-2",
                    isActive && "active"
                  )}
                >
                  <Settings className="w-5 h-5" />
                  <span className="font-medium">Profil Saya</span>
                </NavLink>
                {isInstallable && !isInstalled && (
                  <Button
                    variant="ghost"
                    className="w-full justify-start gap-3 mt-2 text-primary hover:text-primary hover:bg-primary/10"
                    onClick={() => {
                      handleInstallApp();
                      setSidebarOpen(false);
                    }}
                    data-testid="install-app-btn-mobile"
                  >
                    <Download className="w-5 h-5" />
                    Install Aplikasi
                  </Button>
                )}
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3 mt-2 text-muted-foreground hover:text-destructive"
                  onClick={handleLogout}
                >
                  <LogOut className="w-5 h-5" />
                  Keluar
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className="md:pl-64 pt-16 min-h-screen pb-24 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {children}
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="mobile-nav md:hidden">
        <div className="flex justify-around py-2">
          {navItems.slice(0, 4).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => cn(
                "mobile-nav-item",
                isActive && "active"
              )}
            >
              <item.icon className="w-5 h-5" />
              <span className="text-xs">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Push Notification Prompt */}
      <PushNotificationPrompt />
    </div>
  );
};

export default Layout;
